// Gráficos dibujados en SVG, sin librerías: un gráfico de línea es geometría básica
// y una dependencia de gráficos pesaría más que toda la aplicación. Los colores se
// pasan como variables CSS ('var(--success)') para que sigan saliendo de tokens.css.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icono } from './Icono';
import { Tarjeta } from './Tarjeta';
import { useSerieAnimada } from './animation';

const CORAL = 'var(--coral-500)';

interface PropsPanelGrafico {
  titulo: string;
  subtitulo?: string;
  acciones?: ReactNode;
  children: ReactNode;
}

export function PanelGrafico({ titulo, subtitulo, acciones, children }: PropsPanelGrafico) {
  return (
    <Tarjeta relleno={22}>
      <div className="grafico__cabecera">
        <div>
          <h3 className="grafico__titulo">{titulo}</h3>
          {subtitulo && <div className="grafico__subtitulo">{subtitulo}</div>}
        </div>
        {acciones}
      </div>
      {children}
    </Tarjeta>
  );
}

export function NotaGrafico({ children }: { children: ReactNode }) {
  return (
    <div className="grafico__nota">
      <Icono nombre="info" tamano={13} />
      <span>{children}</span>
    </div>
  );
}

function useAncho(inicial: number) {
  const contenedor = useRef<HTMLDivElement>(null);
  const [ancho, setAncho] = useState(inicial);

  useEffect(() => {
    const nodo = contenedor.current;
    if (!nodo) return undefined;
    const observador = new ResizeObserver(([entrada]) => {
      if (entrada) setAncho(Math.max(280, entrada.contentRect.width));
    });
    observador.observe(nodo);
    return () => { observador.disconnect(); };
  }, []);

  return { contenedor, ancho };
}

interface PropsGraficoLinea {
  datos: readonly number[];
  etiquetas: readonly string[];
  alto?: number;
  color?: string;
  formatearY?: (valor: number) => string;
  /** Dibuja una línea punteada de referencia. */
  promedio?: number;
  descripcion?: string;
}

const MARGEN = { arriba: 12, derecha: 14, abajo: 26, izquierda: 42 };
const MARCAS_Y = 4;

export function GraficoLinea({
  datos, etiquetas, alto = 200, color = CORAL, formatearY = String, promedio, descripcion,
}: PropsGraficoLinea) {
  const [activo, setActivo] = useState<number | null>(null);
  const { contenedor, ancho } = useAncho(560);
  const serie = useSerieAnimada(datos);

  if (serie.length === 0) {
    return <div className="grafico__vacio">Todavía no hay datos para este período.</div>;
  }

  const w = ancho - MARGEN.izquierda - MARGEN.derecha;
  const h = alto - MARGEN.arriba - MARGEN.abajo;
  const rango = Math.max(...serie, 1);
  const yDe = (v: number) => MARGEN.arriba + h - (v / rango) * h;

  const puntos = serie.map((v, i) => ({
    x: MARGEN.izquierda + (serie.length === 1 ? w / 2 : (i / (serie.length - 1)) * w),
    y: yDe(v),
    etiqueta: etiquetas[i] ?? '',
  }));

  const linea = puntos.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const primero = puntos[0];
  const ultimo = puntos[puntos.length - 1];
  const area = primero && ultimo
    ? `${linea} L${ultimo.x},${MARGEN.arriba + h} L${primero.x},${MARGEN.arriba + h} Z`
    : '';
  const marcado = activo === null ? undefined : puntos[activo];

  return (
    <div ref={contenedor} className="grafico-linea">
      <svg
        width="100%"
        height={alto}
        viewBox={`0 0 ${ancho} ${alto}`}
        className="grafico-linea__lienzo"
        role="img"
        aria-label={descripcion ?? etiquetas.map((e, i) => `${e}: ${formatearY(datos[i] ?? 0)}`).join(', ')}
      >
        {Array.from({ length: MARCAS_Y + 1 }, (_, i) => {
          const y = MARGEN.arriba + h - (i / MARCAS_Y) * h;
          return (
            <g key={i}>
              <line x1={MARGEN.izquierda} y1={y} x2={MARGEN.izquierda + w} y2={y} className="grafico__guia" />
              <text x={MARGEN.izquierda - 8} y={y + 4} textAnchor="end" className="grafico__eje grafico__eje--numero">
                {formatearY(Math.round((rango / MARCAS_Y) * i * 10) / 10)}
              </text>
            </g>
          );
        })}

        {promedio !== undefined && (
          <line
            x1={MARGEN.izquierda} y1={yDe(promedio)} x2={MARGEN.izquierda + w} y2={yDe(promedio)}
            className="grafico__promedio"
          />
        )}

        <path d={area} style={{ fill: color }} opacity="0.09" />
        <path d={linea} fill="none" style={{ stroke: color }} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

        {puntos.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={activo === i ? 5.5 : 3.5} className="grafico-linea__punto" style={{ stroke: color }} />
            {/* Franja invisible, mucho más ancha que el punto, para que sea fácil apuntarle */}
            <rect
              x={p.x - w / (serie.length * 2)} y={MARGEN.arriba} width={w / serie.length} height={h}
              fill="transparent"
              onMouseEnter={() => { setActivo(i); }}
              onMouseLeave={() => { setActivo(null); }}
            />
            <text x={p.x} y={alto - 6} textAnchor="middle" className="grafico__eje">{p.etiqueta}</text>
          </g>
        ))}
      </svg>

      {marcado && activo !== null && (
        <div
          className="grafico-linea__globo"
          style={{ left: Math.min(Math.max(marcado.x - 50, 0), ancho - 110), top: marcado.y - 46 }}
        >
          <strong>{formatearY(Math.round(datos[activo] ?? 0))}</strong>
          <span>{marcado.etiqueta}</span>
        </div>
      )}
    </div>
  );
}

export interface ItemBarra {
  etiqueta: string;
  valor: number;
  color?: string;
}

interface PropsGraficoBarras {
  items: readonly ItemBarra[];
  color?: string;
  formatear?: (valor: number) => string;
  vacio?: string;
}

export function GraficoBarras({
  items, color = CORAL, formatear = String, vacio = 'Sin datos en este período.',
}: PropsGraficoBarras) {
  if (items.length === 0) return <div className="grafico__vacio">{vacio}</div>;
  const maximo = Math.max(...items.map((i) => i.valor), 1);

  return (
    <ul className="barras">
      {items.map((it) => (
        <li key={it.etiqueta}>
          <div className="barras__fila">
            <span className="barras__etiqueta">{it.etiqueta}</span>
            <span className="barras__valor">{formatear(it.valor)}</span>
          </div>
          <div className="barras__riel" aria-hidden="true">
            <div className="barras__barra" style={{ width: `${(it.valor / maximo) * 100}%`, background: it.color ?? color }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

export interface ParteAnillo {
  etiqueta: string;
  valor: number;
  color: string;
}

interface PropsGraficoAnillo {
  partes: readonly ParteAnillo[];
  tamano?: number;
  centro?: { valor: ReactNode; texto: string };
}

export function GraficoAnillo({ partes, tamano = 150, centro }: PropsGraficoAnillo) {
  const total = partes.reduce((suma, p) => suma + p.valor, 0);
  const radio = tamano / 2 - 12;
  const circunferencia = 2 * Math.PI * radio;
  const porcentaje = (valor: number) => (total ? Math.round((valor / total) * 100) : 0);

  let acumulado = 0;
  const arcos = partes.map((p) => {
    const largo = total ? (p.valor / total) * circunferencia : 0;
    const arco = { ...p, largo, desfase: acumulado };
    acumulado += largo;
    return arco;
  });

  return (
    <div className="anillo">
      <div className="anillo__figura" style={{ width: tamano, height: tamano }}>
        <svg width={tamano} height={tamano} className="anillo__lienzo" aria-hidden="true">
          <circle cx={tamano / 2} cy={tamano / 2} r={radio} className="anillo__fondo" strokeWidth="14" />
          {arcos.map((a) => (
            <circle
              key={a.etiqueta}
              cx={tamano / 2} cy={tamano / 2} r={radio}
              fill="none" strokeWidth="14"
              strokeDasharray={`${a.largo} ${circunferencia - a.largo}`}
              strokeDashoffset={-a.desfase}
              className="anillo__arco"
              style={{ stroke: a.color }}
            />
          ))}
        </svg>
        <div className="anillo__centro">
          <div className="anillo__total">{centro?.valor ?? total}</div>
          <div className="anillo__texto">{centro?.texto ?? 'turnos'}</div>
        </div>
      </div>
      <ul className="anillo__leyenda">
        {partes.map((p) => (
          <li key={p.etiqueta} className="anillo__item">
            <span className="anillo__muestra" style={{ background: p.color }} />
            <span className="anillo__etiqueta">{p.etiqueta}</span>
            <span className="anillo__valor">{p.valor}</span>
            <span className="anillo__porcentaje">{porcentaje(p.valor)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
